import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Calendar,
  ClipboardList,
  Clock,
  DollarSign,
  Timer
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import weeklyTimesheetService from '../../services/weeklyTimesheetService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const WeeklyTimesheet = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTimesheets: 0,
    draftTimesheets: 0,
    submittedTimesheets: 0,
    underReviewTimesheets: 0,
    approvedTimesheets: 0,
    rejectedTimesheets: 0,
    totalHours: 0,
    totalBillableHours: 0,
    totalOvertimeHours: 0,
    totalBillingAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [timesheetsResponse, statsResponse] = await Promise.all([
        weeklyTimesheetService.getAllWeeklyTimesheets(),
        weeklyTimesheetService.getWeeklyTimesheetStats()
      ])
      
      if (timesheetsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (timesheetsResponse.data || []).map(timesheet => ({
          ...timesheet,
          id: timesheet._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (timesheetsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No weekly timesheet records found. Create your first weekly timesheet!')
        }
      } else {
        toast.error(timesheetsResponse.message || 'Failed to load weekly timesheets')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading weekly timesheet records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view weekly timesheets')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load weekly timesheet records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatHours = (hours) => {
    if (!hours) return '0.0h'
    return `${parseFloat(hours).toFixed(1)}h`
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatWeekRange = (weekStartDate, weekEndDate) => {
    if (!weekStartDate || !weekEndDate) return '-'
    const start = new Date(weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const end = new Date(weekEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${start} - ${end}`
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Submitted': 'pending',
      'Under Review': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Invoiced': 'active'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'timesheetNumber',
      header: 'Timesheet Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'weekStartDate',
      header: 'Week',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'center' }}>
          {formatWeekRange(getValue(), row.original.weekEndDate)}
          {row.original.weekNumber && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>Week {row.original.weekNumber}, {row.original.year}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'totalHours',
      header: 'Total Hours',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {formatHours(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'billableHours',
      header: 'Billable Hours',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatHours(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'overtimeHours',
      header: 'Overtime',
      cell: ({ getValue }) => {
        const hours = parseFloat(getValue() || 0)
        return (
          <div style={{ textAlign: 'center', color: hours > 0 ? '#dc3545' : '#6b7280' }}>
            {formatHours(hours)}
          </div>
        )
      },
    },
    {
      accessorKey: 'totalBillingAmount',
      header: 'Billing Amount',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/timesheets/weekly-timesheet/form')
  }

  const handleEdit = (timesheetData) => {
    navigate('/timesheets/weekly-timesheet/edit', { state: { timesheetData } })
  }

  const handleView = (timesheetData) => {
    navigate('/timesheets/weekly-timesheet/view', { state: { timesheetData } })
  }

  const handleDelete = (timesheetData) => {
    setDeleteItem(timesheetData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting weekly timesheet...')
      const response = await weeklyTimesheetService.deleteWeeklyTimesheet(deleteItem._id)
      if (response.success) {
        toast.success('Weekly timesheet deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete weekly timesheet')
      }
    } catch (error) {
      console.error('Error deleting weekly timesheet:', error)
      if (error.response?.status === 404) {
        toast.error('Weekly timesheet not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this weekly timesheet')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete weekly timesheet. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Timesheets', path: '/timesheets' },
          { label: 'Weekly Timesheets', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Weekly Timesheets</h1>
          <p>Weekly timesheet submissions and reviews</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Timesheet
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTimesheets || 0}</div>
          <div className="metric-title">Total Timesheets</div>
          <div className="metric-subtitle">All weekly sheets</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Timer size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Hours</span>
            </div>
          </div>
          <div className="metric-value">{formatHours(stats.totalHours)}</div>
          <div className="metric-title">Total Hours</div>
          <div className="metric-subtitle">Logged time</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Billable</span>
            </div>
          </div>
          <div className="metric-value">{formatHours(stats.totalBillableHours)}</div>
          <div className="metric-title">Billable Hours</div>
          <div className="metric-subtitle">Client billable time</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Timer size={16} />
            </div>
            <div className="metric-trend negative">
              <span>OT</span>
            </div>
          </div>
          <div className="metric-value">{formatHours(stats.totalOvertimeHours)}</div>
          <div className="metric-title">Overtime Hours</div>
          <div className="metric-subtitle">Extra hours logged</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.submittedTimesheets + stats.underReviewTimesheets || 0}</div>
          <div className="metric-title">Pending Approval</div>
          <div className="metric-subtitle">Awaiting review</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Amount</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalBillingAmount)}</div>
          <div className="metric-title">Billing Amount</div>
          <div className="metric-subtitle">Total billing value</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Weekly Timesheet"
        message={`Are you sure you want to delete weekly timesheet "${deleteItem?.timesheetNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default WeeklyTimesheet

